<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { getUnitePuissance } from "@modules/courtage/utils/optim-turpe-v2.helpers"
import { nombreSaisi } from "@modules/courtage/utils/optim-turpe-v2.saisie"
import { LOCALE_SAISIE } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import {
    legendeDesClasses,
    libelleClasseHoraire,
    libelleFormuleTarifaire,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import type {
    OptimTurpeV2Etude,
    OptimTurpeV2PlageKey,
    OptimTurpeV2Segment,
} from "@modules/courtage/types/optim-turpe-v2"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            // M2 de Mélodie : le nom court à l'écran, le terme officiel
            // reste dans la demande et le rapport.
            fta: "Formule d'acheminement (FTA)",
            puissance: "Puissance souscrite ({unite})",
            // Contrat v1.2 §8.2.2 et maquette 2d : le pluriel du registre
            // (« Puissances souscrites par poste »).
            denivele: "Puissances souscrites par poste ({unite})",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    etude: OptimTurpeV2Etude
    /** Affiche le libellé de chaque champ (false = champs nus, ex. en grille). */
    showLabels?: boolean
    /**
     * Les plages à saisir, quand l'appelant les connaît mieux que l'étude :
     * avant le premier lancement (Q0, 08/10/2026), celles du segment. `null` :
     * celles de l'étude.
     */
    plages?: OptimTurpeV2PlageKey[] | null
    /**
     * Lot 4d (contrat v1.2 §8.2.2) : le préfixe des `data-testid`, pour qu'une
     * seconde instance (« Puissances à chiffrer ») se distingue de celle du
     * contrat actuel.
     */
    prefixe?: string
    /** La provenance de la formule, sous son champ (« Lue chez Enedis »). */
    provenanceFta?: string | null
    /** La provenance des puissances, sous leurs champs (« Lues chez Enedis »). */
    provenancePuissances?: string | null
    /** Une note sous les puissances (« Préremplies avec la recommandation : … »). */
    notePuissances?: string | null
}

type FtaOption = { label: string; value: string }

// ================================
// PROPS & MODELS
// ================================
const props = withDefaults(defineProps<Props>(), {
    showLabels: true,
    plages: null,
    prefixe: "optim-turpe-v2",
    provenanceFta: null,
    provenancePuissances: null,
    notePuissances: null,
})

/** FTA scopée au segment (partagée entre « prendre la main » et la surcharge). */
const fta = defineModel<string>("fta", { default: "" })
/** Puissance souscrite unique (C5). */
const puissance = defineModel<number | null>("puissance", { default: null })
/** Dénivelé par plage (C4/C2), objet réactif édité en place. */
const denivele = defineModel<Partial<Record<OptimTurpeV2PlageKey, number>>>(
    "denivele",
    { default: () => ({}) }
)

// ================================
// CONSTANTS
// ================================
/** FTA disponibles par segment (catalogue Enedis, restreint au segment courant). */
const FTA_BY_SEGMENT: Record<OptimTurpeV2Segment, string[]> = {
    C5: ["BTINFCU4", "BTINFMU4", "BTINFCUST", "BTINFLU", "BTINFMUDT"],
    C4: ["BTSUPCU4", "BTSUPLU4"],
    C2: ["HTACU5", "HTALU5"],
}

const PLAGE_ORDER: OptimTurpeV2PlageKey[] = ["PTE", "HPH", "HCH", "HPB", "HCB"]

// ================================
// INTERACTIONS
// ================================
/**
 * Effacement (prod v491) : une plage vidée sort du dénivelé, comme si elle
 * n'avait jamais été saisie ; le champ vidé rend `null` (ou `NaN`, ou une
 * chaîne vide selon le composant), jamais écrit tel quel.
 */
function poserPlage(plage: OptimTurpeV2PlageKey, valeur: unknown): void {
    const nombre = nombreSaisi(valeur)
    if (nombre === null) delete denivele.value[plage]
    else denivele.value[plage] = nombre
}
function poserPuissance(valeur: unknown): void {
    puissance.value = nombreSaisi(valeur)
}

// ================================
// COMPUTED
// ================================
const getSegment = computed<OptimTurpeV2Segment | "">(
    () => props.etude.compteur?.segment ?? ""
)

const getIsC5 = computed(() => getSegment.value === "C5")

/** Unité de puissance souscrite selon le segment (C4/C5 = kVA, C2 = kW). */
const getUnite = computed(() => getUnitePuissance(props.etude))

/**
 * Le nom accessible d'un champ de puissance (rejeu du 20/10/2026) : le poste
 * en clair, son code, puis l'unité, « Heures pleines de saison haute (HPH),
 * kVA ». Sans lui, un lecteur d'écran ne lisait que la valeur (« 88 »).
 */
function nomDuChamp(plage: OptimTurpeV2PlageKey): string {
    const poste = libelleClasseHoraire(plage)
    return `${poste.charAt(0).toUpperCase()}${poste.slice(1)}, ${getUnite.value}`
}

/** Le nom accessible de la puissance unique d'un C5 : « Puissance souscrite, kVA ». */
const getNomPuissance = computed(() => `Puissance souscrite, ${getUnite.value}`)

/** Options FTA scopées au segment courant. */
const getFtaOptions = computed<FtaOption[]>(() => {
    const segment = getSegment.value
    if (!segment) return []
    return (FTA_BY_SEGMENT[segment] ?? []).map((code) => ({
        label: libelleFormuleTarifaire(code),
        value: code,
    }))
})

/** Plages éditables (présentes dans le dénivelé « meilleure » ou « actuelle »). */
/** La légende des classes saisies (05/10/2026). */
const getLegendeClasses = computed(() =>
    legendeDesClasses(getDenivelePlages.value)
)

const getDenivelePlages = computed<OptimTurpeV2PlageKey[]>(() => {
    if (props.plages) return props.plages
    const best =
        props.etude.meilleure?.structure_tarifaire
            ?.puissance_souscrite_soutirage?.denivele ?? {}
    const current =
        props.etude.actuelle?.structure_tarifaire?.puissance_souscrite_soutirage
            ?.denivele ?? {}
    // L17 (vague wording du 14/10/2026) : un C4 n'a pas de poste de pointe.
    const sansPointe = getSegment.value === "C4"
    return PLAGE_ORDER.filter(
        (key) =>
            !(sansPointe && key === "PTE") &&
            (best[key] !== undefined || current[key] !== undefined)
    )
})
</script>

<template>
    <!-- ================================ -->
    <!-- STRUCTURE — FTA + puissance souscrite (partagé pricer / surcharge) -->
    <!-- ================================ -->
    <div class="flex flex-col gap-3">
        <!-- FTA -->
        <div class="flex flex-col gap-1">
            <label
                v-if="showLabels"
                class="text-sm font-medium text-primary-700"
                >{{ t("fta") }}</label
            >
            <BaseSelect
                v-model="fta"
                :options="getFtaOptions"
                option-label="label"
                option-value="value"
                class="w-full"
            />
            <p
                v-if="provenanceFta"
                class="text-xs text-primary-500"
                :data-testid="`${prefixe}-provenance-fta`"
            >
                {{ provenanceFta }}
            </p>
        </div>

        <!-- C5 : puissance unique. Série UX du témoin (2.3 point 8) : l'unité
             s'écrit une fois, dans le libellé ; sans libellé, dans le champ.
             Saisies robustes (20/10/2026) : aucune borne haute sur le champ,
             qui rognait en silence (un C2 à 1 500 kW devenait 999 au premier
             passage dans le champ) ; la borne du segment, les entiers et le
             zéro se refusent par une phrase sous les puissances
             (`refusDesPuissances`). Les décimales restent saisissables pour
             être refusées à la vue : avec `max-fraction-digits="0"`, le champ
             avalait la virgule (« 1500,5 » devenait 15 005, relu le 20/10). -->
        <div v-if="getIsC5" class="flex flex-col gap-1">
            <label
                v-if="showLabels"
                class="text-sm font-medium text-primary-700"
                >{{ t("puissance", { unite: getUnite }) }}</label
            >
            <BaseInputNumber
                :locale="LOCALE_SAISIE"
                :model-value="puissance"
                :aria-label="getNomPuissance"
                :min="0"
                :max-fraction-digits="2"
                :suffix="showLabels ? undefined : ` ${getUnite}`"
                class="w-full min-w-0"
                input-class="!text-primary-900 font-medium w-full min-w-0"
                :data-testid="`${prefixe}-puissance`"
                @update:model-value="poserPuissance"
            />
            <p
                v-if="provenancePuissances"
                class="text-xs text-primary-500"
                :data-testid="`${prefixe}-provenance-puissances`"
            >
                {{ provenancePuissances }}
            </p>
            <p
                v-if="notePuissances"
                class="text-xs text-primary-500"
                :data-testid="`${prefixe}-note-puissances`"
            >
                {{ notePuissances }}
            </p>
        </div>

        <!-- C4 / C2 : dénivelé par plage -->
        <div v-else class="flex flex-col gap-1">
            <label
                v-if="showLabels"
                class="text-sm font-medium text-primary-700"
                >{{ t("denivele", { unite: getUnite }) }}</label
            >
            <div
                class="grid grid-cols-2 gap-3 md:grid-cols-4"
                data-legende-classes
            >
                <div
                    v-for="plage in getDenivelePlages"
                    :key="plage"
                    class="flex min-w-0 flex-col gap-1"
                >
                    <span class="text-xs font-medium text-primary-900">{{
                        plage
                    }}</span>
                    <BaseInputNumber
                        :locale="LOCALE_SAISIE"
                        :model-value="denivele[plage]"
                        :aria-label="nomDuChamp(plage)"
                        :min="0"
                        :max-fraction-digits="2"
                        :suffix="showLabels ? undefined : ` ${getUnite}`"
                        class="w-full min-w-0"
                        input-class="!text-primary-900 font-medium w-full min-w-0"
                        :data-testid="`${prefixe}-denivele-${plage}`"
                        @update:model-value="poserPlage(plage, $event)"
                    />
                </div>
            </div>
            <p
                v-if="getLegendeClasses"
                class="text-xs text-primary-500"
                data-testid="optim-turpe-v2-structure-legende"
            >
                {{ getLegendeClasses }}
            </p>
            <p
                v-if="provenancePuissances"
                class="text-xs text-primary-500"
                :data-testid="`${prefixe}-provenance-puissances`"
            >
                {{ provenancePuissances }}
            </p>
            <p
                v-if="notePuissances"
                class="text-xs text-primary-500"
                :data-testid="`${prefixe}-note-puissances`"
            >
                {{ notePuissances }}
            </p>
        </div>
    </div>
</template>
